/**
 * Who performed a write, as stored in `createdBy` / `updatedBy` / audit columns.
 *
 * - a signed-in user: `<userId>`
 * - an AIMS grant acting for a user: `<userId>/aims:<grantId>`
 * - an AIMS grant acting for a tenant with no user: `aims:<grantId>`
 *
 * Readers that resolve these columns to a user go through `userIdFromActorLabel`
 * so grant-written rows still resolve to their subject.
 */
export type ActorLabelParts = { userId: string | null; grantId: string | null };

/** How a grant acting with no user is shown to people. */
export const GRANT_ACTOR_DISPLAY_NAME = "Scout";

export const GRANT_PREFIX = "aims:";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function formatActorLabel({ userId, grantId }: ActorLabelParts): string {
  if (grantId === null) {
    if (userId === null) throw new Error("An actor label needs a user or a grant");
    return userId;
  }
  const grantPart = `${GRANT_PREFIX}${grantId}`;
  return userId === null ? grantPart : `${userId}/${grantPart}`;
}

export function parseActorLabel(label: string): ActorLabelParts {
  const slash = label.indexOf(`/${GRANT_PREFIX}`);
  if (slash >= 0) {
    return { userId: label.slice(0, slash), grantId: label.slice(slash + 1 + GRANT_PREFIX.length) };
  }
  if (label.startsWith(GRANT_PREFIX)) {
    return { userId: null, grantId: label.slice(GRANT_PREFIX.length) };
  }
  return { userId: label, grantId: null };
}

/** The user a label refers to, or null for a grant acting without one. Non-user writers such as `workflow-manager` come back unchanged. */
export function userIdFromActorLabel(label: string): string | null {
  return parseActorLabel(label).userId;
}

/** The user a label refers to when it can fill a `User` foreign key; null for grants without a user and for non-user writers. */
export function userForeignKeyFromActorLabel(label: string | null | undefined): string | null {
  const userId = label ? userIdFromActorLabel(label) : null;
  return userId !== null && UUID.test(userId) ? userId : null;
}
